<template lang="pug">
  div(:class="[trustPilotError ? 'flex justify-center' : $style.carousel, $style.trustPilotLayout, `carousel-${blockId}`]")
    .rating
      .rating-head
        .av-heading {{ $t('trustpilot.excellent') }}
        img(
          src="~/assets/img/trustpilot/brand/stars-4.5.svg"
          loading="lazy"
          width="128"
          height="24"
          alt="Trustpilot rating")
      p
        span(v-html="ratingString")
        a(:href="trustpilotURL" target="_blank")
          img(
            src="~/assets/img/trustpilot/brand/brandmark_gr-blk.svg"
            loading="lazy"
            width="98"
            height="24"
            alt="Trustpilot logo")
    swiper-container.swiper.overflow-hidden(
      v-show="!trustPilotError"
      ref="swiper"
      :init.prop="false"
      :loop.prop="true"
      :slides-per-view.prop="1"
      :space-between.prop="32"
      :breakpoints.prop="breakpointConfig"
      :pagination.prop="paginationConfig"
      :navigation.prop="navigationConfig"
    )
      swiper-slide(
        v-show="slides.length"
        v-for="(review, key) in slides"
        :key="`avtcs-${key}`"
      )
        div(class="author") {{ $truncate(review.consumer.displayName, 30) }}
        div(class="info")
          div
            img(
              src="~/assets/img/trustpilot/brand/ratings_5star.svg"
              loading="lazy"
              width="128"
              height="24"
              alt="Trustpilot rating")
          span {{ formatDistance(review.createdAt) }}
        div(class="reviewContent")
          div(class="reviewTitle")
            a(
              v-if="review.reviewUrl"
              :href="review.reviewUrl"
            ) {{ $truncate(review.title, 30) }}
            a(
              v-else-if="review.links && review.links[0] && review.links[0].href"
              :href="reviewHref(review.links[0].href)"
            ) {{ $truncate(review.title, 30) }}
            span(v-else) {{ $truncate(review.title, 30) }}
          p {{ $truncate(review.text, 120) }}
    .swiper-navigation-container(v-show="slides.length")
      .swiper-button-prev
      .swiper-pagination
      .swiper-button-next
</template>


<script lang="ts">
import { defineComponent, onMounted, ref, nextTick } from "vue"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import axios from "axios"
import { intlFormatDistance } from "date-fns"
import { patternCheck } from "~/utils/block"
import { LOCALE } from "~/constants/locale"
import type { SwiperContainer } from "swiper/element"

interface ComponentData {
  slides: Record<string, any>[]
  reviews: Record<string, any>[]
  trustPilotError: boolean
  filterReviewsByStars: number
  trustpilotURL: string
  trustpilotTotalReviews: string
  trustpilotExcellentReviewsRounded: string
  trustpilotRating: number
  breakpointConfig: Record<number, any>
  paginationConfig: Record<string, any>
  navigationConfig: Record<string, any>
}

export default defineComponent({
  name: "TrustpilotCarousel",

  inject: {
    trustPilotReviewsFilter: {
      from: "trustPilotReviewsFilter"
    }
  },

  props: {
    block: {
      type: Object,
      required: true
    },
    blockId: {
      type: String,
      required: true
    }
  },

  setup() {
    const swiper: Ref<SwiperContainer | null> = ref(null)

    onMounted(async () => {
      if (import.meta.client) {
        nextTick(() => {
          setTimeout(() => {
            // I'm not a fan of this solution, but is the most reliable for now
            // Swiper doesn't recognise the review items
            // due to other API responses being resolved first, causing a delay on loading reviews.
            swiper.value?.initialize()
          }, 500)
        })
      }
    })

    return { swiper }
  },

  data(): ComponentData {
    return {
      slides: [],
      trustPilotError: false,
      filterReviewsByStars: 5,
      reviews: [],
      trustpilotURL: "",
      trustpilotTotalReviews: "158,000+",
      trustpilotExcellentReviewsRounded: "0",
      trustpilotRating: 4.6,
      breakpointConfig: {
        480: {
          slidesPerView: 1,
          spaceBetween: 24
        },
        568: {
          slidesPerView: 2,
          spaceBetween: 16
        },
        768: {
          slidesPerView: 3,
          spaceBetween: 24
        },
        992: {
          slidesPerView: 2,
          spaceBetween: 30
        },
        1200: {
          slidesPerView: 2,
          spaceBetween: 30
        },
        1500: {
          slidesPerView: 3,
          spaceBetween: 32
        }
      },
      paginationConfig: {
        el: `.carousel-${this.blockId} .swiper-pagination`,
        type: "bullets",
        clickable: true
      },
      navigationConfig: {
        nextEl: `.carousel-${this.blockId} .swiper-button-next`,
        prevEl: `.carousel-${this.blockId} .swiper-button-prev`
      }
    }
  },

  computed: {
    title(): string {
      return this.block.innerBlocks[0].innerBlocks[0].innerBlocks[0].innerHtml.replace(
        new RegExp("<h2[^>]*>+(.*)<\/h2>"),
        "$1"
      )
    },

    ratingString(): string {
      const locale = this.$i18n.locale || "en"
      const rating = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }).format(this.trustpilotRating)

      const totalReviews = formatNumberWithLocale(this.trustpilotTotalReviews, this.$i18n.locale)
      const template = this.$t("trustpilot.rating").toString()

      return template.replace("RATING", rating).replace("REVIEWCOUNT", totalReviews)
    },
  },

  async created() {
    const trustPilotConfig = this.$config.public.trustPilot
    let trustPilotId = trustPilotConfig.id
    let trustPilotURL = trustPilotConfig.url
    let trustPilotAPIKey = trustPilotConfig.apiKey
    if (this.$i18n.locale !== LOCALE.UK) {
      const upperCaseLocale = this.$i18n.locale.toUpperCase()
      trustPilotId = trustPilotConfig[`id${upperCaseLocale}`]
      trustPilotURL = trustPilotConfig[`url${upperCaseLocale}`]
      trustPilotAPIKey = trustPilotConfig[`apiKey${upperCaseLocale}`]
    }

    try {
      const axiosInstance = axios.create()
      const response = await axiosInstance.get(`https://api.trustpilot.com/v1/business-units/${trustPilotId}`, {
        params: {
          apikey: trustPilotAPIKey
        }
      })

      if (response.status === 200 && response.data) {
        if (
          !response.data ||
          !response.data.score.trustScore ||
          !response.data.numberOfReviews.total
        ) {
          this.trustPilotError = true
          return
        }
        this.trustpilotURL = trustPilotURL
        this.trustpilotRating = response.data.score.trustScore
        this.trustpilotTotalReviews = Number(response.data.numberOfReviews.total)
        this.trustpilotExcellentReviewsRounded = Number(
          Math.floor(response.data.numberOfReviews.fiveStars / 1000) * 1000
        ).toLocaleString()
      }

      const filter = this.trustPilotReviewsFilter
      const category = filter?.category?.[0]
      const limit = 6

      if (category) {
        const baseURL = import.meta.server
          ? this.$config.public.baseApiUrl.server
          : this.$config.public.baseApiUrl.client
        const responseReviews = await axiosInstance.get(`${baseURL}/api/v4/trustpilot-reviews`, {
          params: {
            category: category,
            limit: limit
          }
        })

        if (responseReviews.status === 200 && responseReviews.data) {
          const reviewsData = responseReviews.data.result || responseReviews.data.results || responseReviews.data || []

          this.reviews = Array.isArray(reviewsData) ? reviewsData : []
          this.slides = this.reviews.slice(0, 6)
        }
      } else {
        const responseReviews = await axiosInstance.get(`https://api.trustpilot.com/v1/business-units/${trustPilotId}/reviews`, {
          params: {
            apikey: trustPilotConfig.apiKey,
            stars: [this.filterReviewsByStars]
          }
        })

        if (responseReviews.status === 200 && responseReviews.data) {
          this.reviews = responseReviews.data.reviews
          this.slides = this.reviews.slice(0, 6)
        }
      }
    } catch (err) {
      this.trustPilotError = true
      console.log("Trustpilot error", err)
    }
  },

  methods: {
    patternCheck,

    reviewHref(href: string): string {
      return href.replace("api.trustpilot.com/v1/reviews/", "www.trustpilot.com/reviews/")
    },

    formatDistance(date: string): string {
      const options: any = {
        style: "narrow",
        addSuffix: false,
        numeric: "always"
      }
      if (this.$i18n.locale !== LOCALE.UK) {
        options.locale = this.$i18n.locale
      }
      return intlFormatDistance(new Date(date), new Date(), options)
    }
  }
})
</script>

<style lang="scss" module>
/* padding: 0; should be applied to the containing section! */
/* overflow: auto; should be applied to .content child of the containing section */
.trustPilotLayout {
  :global {
    .smooth-scroll {
      scroll-behavior: smooth;
    }

    .rating {
      @apply mx-ds-md mb-ds-lg;

      .rating-head {
        @apply flex mb-ds-sm items-center justify-center;

        .av-heading {
          @apply my-0;
          text-transform: none;
        }

        img {
          @apply w-auto h-ds-ml;
          margin-left: 7px;
        }
      }

      p {
        @apply mb-0 text-rebrandSm text-center items-center content-center;

        & > * {
          @apply align-middle;
        }

        span {
          @apply inline-block;
        }

        img {
          @apply inline-block w-auto h-ds-ml ml-ds-xxs mb-ds-sm;
        }
      }
    }
  }
}

.carousel {
  @apply overflow-hidden;
  width: calc(100vw - 16px); /** view width less right padding */

  :global {
    .swiper {
      @apply w-full mt-0;

      & > swiper-slide {
        @apply px-ds-ml py-ds-md relative overflow-hidden flex flex-col h-auto bg-white border border-gray-300 rounded-xl box-border;
        width: 328px;
        min-height: 195px;
        scroll-snap-align: start;

        &:nth-child(1),
        &:nth-child(2),
        &:nth-child(3) {
          display: block;
        }

        .author {
          @apply text-rebrandSm mx-0 my-ds-xxs;
          font-weight: normal;
          text-transform: none;
        }

        .info {
          @apply flex flex-row justify-between;

          div {
            @apply flex text-left items-center;
            justify-content: left;
            width: 45%;
          }

          img {
            @apply h-ds-ml;
          }

          span {
            @apply flex text-base items-center;
            justify-content: right;
            font-weight: 700;
            font-style: italic;
          }
        }

        .reviewContent {
          @apply pt-ds-md;

          .reviewTitle {
            @apply text-rebrandSm pt-ds-xxs pb-ds-xxxs mb-0;
            font-weight: 600;
            text-transform: none;

            a {
              text-decoration: none;
              color: inherit;
            }
          }

          p {
            @apply text-base;
          }
        }
      }
    }

    .swiper-navigation-container {
      @apply flex gap-ds-sm items-center mt-ds-ml mb-ds-sm mr-ds-md justify-between md:justify-center lg:justify-start z-[1];

      .swiper-button-prev,
      .swiper-button-next {
        @apply relative z-[1] min-h-ds-xl min-w-ds-xl h-ds-xl w-ds-xl cursor-pointer flex items-center justify-center rounded-full text-black border border-solid border-black border-opacity-20 bg-white inset-auto mt-auto;

        &:after {
          @apply text-base;
        }
      }

      .swiper-pagination.swiper-pagination-clickable.swiper-pagination-bullets.swiper-pagination-horizontal {
        @apply z-[1];
      }

      .swiper-pagination {
        @apply relative flex gap-ds-sm justify-center mx-ds-sm w-auto mt-ds-md;

        .swiper-pagination-bullet {
          @apply h-ds-md w-ds-md rounded-xl border border-solid border-black border-opacity-20 bg-opacity-10 bg-black;
        }

        .swiper-pagination-bullet-active {
          @apply opacity-100 bg-green-darker z-[1];
        }

        .swiper-pagination-bullets {
          @apply z-[1];
        }
      }
    }

    .buttons {
      @apply flex justify-between mr-ds-md;

      &.only-pips {
        @apply justify-center;
      }

      & > div {
        @apply flex items-start gap-ds-md;

        & > span {
          @apply w-ds-md h-ds-md mt-ds-xl rounded-xl;
          border: 1px solid rgba(0, 0, 0, 0.2);
          background-color: rgba(43, 43, 43, 0.1);

          &.active-slide {
            @apply bg-green-darker;
          }
        }
      }
    }

    .scroll-btn {
      @apply h-ds-xl w-ds-xl mt-ds-lg items-center justify-center inline-flex bg-white border rounded-full border-gray-300 text-gray-800;

      &:disabled {
        @apply bg-gray-300 text-gray-500;
      }
    }
  }
}

@screen md {
  .carousel {
    width: calc(100vw - 32px);
    :global(.buttons) {
      @apply mr-ds-lg;
    }
  }
}

@screen lg {
  .carousel {
    @apply grid gap-x-ds-xxl;
    width: calc(100vw - 80px);
    grid-template-columns: minmax(250px, 300px) 1fr;
    grid-template-areas:
      "rating carousel"
      "gap control";

    > *:nth-child(1) {
      grid-area: rating;
    }

    > *:nth-child(2) {
      grid-area: carousel;
    }

    > *:nth-child(3) {
      grid-area: control;
    }

    :global {
      .rating {
        @apply mx-auto my-0 self-center;

        p {
          @apply text-rebrandMd;
        }
      }

      .swiper {
        & > swiper-slide {
          .author {
            @apply text-rebrandMd;
          }

          .info {
            span {
              @apply text-rebrandSm;
            }
          }

          .reviewContent {
            .reviewTitle {
              @apply text-rebrandMd;
            }

            p {
              @apply text-rebrandSm;
            }
          }
        }
      }

      .buttons {
        @apply m-0;
        width: 360px;
      }
    }
  }
}

@screen xl {
  .carousel {
    width: calc(100vw - 96px);
    grid-template-columns: minmax(200px, 460px) 1fr;
  }
}

@screen rebrandLg {
  .carousel {
    width: calc(50vw + 660px);
  }
}
</style>
